import { useRef } from "react";
import Project from "./Project";
import type {
  Project as ProjectType,
  EditingProject,
  EditingActionItem,
} from "./types";

interface ProjectListProps {
  projects: ProjectType[];
  onAddNew: () => void;
  onEditProject: (projectIndex: number, fieldIndex: number) => void;
  onEditActionItem: (
    projectId: number,
    actionIndex: number,
    fieldIndex: number,
  ) => void;
  onAddNewActionItem: (projectId: number) => void;
  onToggleExpansion: (projectId: number) => void;
  onActionItemStatusToggle?: (
    projectId: number,
    actionIndex: number,
    newStatus: "actionable" | "done" | "not actionable",
  ) => void;
  onProjectFieldChange?: (field: keyof EditingProject, value: string) => void;
  onProjectSave?: () => void;
  onProjectCancel?: () => void;
  onActionItemFieldChange: (
    field: keyof EditingActionItem,
    value: string | number,
  ) => void;
  onActionItemSave: () => void;
  onActionItemCancel: () => void;
  focusState: {
    projectIndex: number;
    actionItemIndex: number;
    expandedProjectId: number | null;
    isAddingNewProject: boolean;
  };
  editingProject?: EditingProject;
  editingActionItem?: EditingActionItem;
  projectError?: string | null;
  className?: string;
}

export default function ProjectList({
  projects,
  onAddNew,
  onEditProject,
  onEditActionItem,
  onAddNewActionItem,
  onToggleExpansion,
  onActionItemStatusToggle,
  onProjectFieldChange,
  onProjectSave,
  onProjectCancel,
  onActionItemFieldChange,
  onActionItemSave,
  onActionItemCancel,
  focusState,
  editingProject,
  editingActionItem,
  projectError,
  className = "",
}: ProjectListProps) {
  const projectNameRef = useRef<HTMLInputElement>(null);
  const projectOutcomeRef = useRef<HTMLTextAreaElement>(null);

  const isEditingNewProject = editingProject?.id === -1;

  const handleNewProjectKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onProjectSave?.();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onProjectCancel?.();
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* New Project Card */}
      <div
        className={`border-2 border-dashed rounded-lg cursor-pointer focus:outline-none ${
          focusState.isAddingNewProject
            ? "ring-2 ring-blue-300 bg-blue-50 border-blue-300 shadow-lg"
            : "border-gray-300 hover:border-blue-300 bg-gray-50"
        }`}
        data-new-project
        tabIndex={0}
        onClick={onAddNew}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onAddNew();
          }
        }}
      >
        <div className="p-4">
          {isEditingNewProject ? (
            <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
              {projectError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {projectError}
                </div>
              )}
              <input
                ref={projectNameRef}
                type="text"
                placeholder="New project name"
                value={editingProject?.name || ""}
                onChange={(e) => onProjectFieldChange?.("name", e.target.value)}
                onKeyDown={handleNewProjectKeyDown}
                className="w-full p-2 border border-blue-500 rounded font-medium text-lg"
              />
              <textarea
                ref={projectOutcomeRef}
                placeholder="Desired outcome"
                value={editingProject?.desiredOutcome || ""}
                onChange={(e) =>
                  onProjectFieldChange?.("desiredOutcome", e.target.value)
                }
                onKeyDown={handleNewProjectKeyDown}
                className="w-full p-2 border border-blue-500 rounded resize-none"
                rows={2}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center text-gray-500">
              <span className="text-lg">+ Add New Project</span>
            </div>
          )}
        </div>
      </div>

      {/* Existing Projects */}
      {projects.map((project, projectIndex) => (
        <Project
          key={project.id}
          project={project}
          projectIndex={projectIndex}
          isExpanded={focusState.expandedProjectId === project.id}
          isFocused={focusState.projectIndex === projectIndex}
          onToggleExpansion={onToggleExpansion}
          onEditProject={onEditProject}
          onEditActionItem={onEditActionItem}
          onAddNewActionItem={onAddNewActionItem}
          onActionItemStatusToggle={onActionItemStatusToggle}
          onActionItemFieldChange={onActionItemFieldChange}
          onActionItemSave={onActionItemSave}
          onActionItemCancel={onActionItemCancel}
          editingProject={editingProject}
          editingActionItem={editingActionItem}
          onProjectFieldChange={onProjectFieldChange}
          onProjectSave={onProjectSave}
          onProjectCancel={onProjectCancel}
          actionItemFocusState={{
            itemIndex: focusState.actionItemIndex,
            isAddingNew: focusState.actionItemIndex === -1,
          }}
        />
      ))}
    </div>
  );
}
